/**
 * Mobile Bottom Sheets
 *
 * Agrega swipe-to-close a los drawers mobile.
 *
 * Reglas:
 * - Solo funciona <= 749px.
 * - Solo arrastra hacia abajo.
 * - Si existe contenido con scroll, primero hace scroll normalmente.
 * - El drawer empieza a bajar únicamente cuando el scroll está arriba.
 * - Si supera el threshold, utiliza el cierre nativo del componente.
 * - Si no supera el threshold, regresa a su posición.
 */

(() => {
    "use strict";

    const MOBILE_QUERY =
        "(max-width: 749px)";

    const SHEET_SELECTORS = [
        /*
         * Dialogs genéricos.
         */
        "dialog .drawer__inner",

        /*
         * Cart drawer.
         */
        ".drawer--cart .drawer__inner",

        /*
         * Complete Look.
         */
        ".nosto-complete-look__drawer-inner",

        /*
         * Filtros.
         */
        "[data-collection-sidebar].collection__sidebar__slide-out",
    ];

    const SCROLLABLE_SELECTORS = [
        ".drawer__body",
        ".collection__filters",
        "[data-scroll-lock-scrollable]",
    ];

    class MobileBottomSheet {
        constructor(sheet) {
            this.sheet = sheet;

            this.startX = 0;
            this.currentX = 0;

            this.startY = 0;
            this.currentY = 0;
            this.startTime = 0;

            this.isDragging = false;
            this.hasMoved = false;
            this.ignoreGesture = false;

            this.activeScrollable = null;
            this.gestureDirection = null;

            this.onTouchStart =
                this.onTouchStart.bind(this);

            this.onTouchMove =
                this.onTouchMove.bind(this);

            this.onTouchEnd =
                this.onTouchEnd.bind(this);

            this.bind();
        }

        bind() {
            this.sheet.setAttribute(
                "data-mobile-bottom-sheet",
                ""
            );

            this.sheet.addEventListener(
                "touchstart",
                this.onTouchStart,
                {
                    passive: true,
                }
            );

            this.sheet.addEventListener(
                "touchmove",
                this.onTouchMove,
                {
                    passive: false,
                }
            );

            this.sheet.addEventListener(
                "touchend",
                this.onTouchEnd,
                {
                    passive: true,
                }
            );

            this.sheet.addEventListener(
                "touchcancel",
                this.onTouchEnd,
                {
                    passive: true,
                }
            );
        }

        isMobile() {
            return window
                .matchMedia(MOBILE_QUERY)
                .matches;
        }

        /*
         * Encuentra qué elemento debe considerarse
         * scrollable para el punto donde inició el gesto.
         */
        findScrollable(target) {
            const closestScrollable =
                target.closest(
                    SCROLLABLE_SELECTORS.join(",")
                );

            if (
                closestScrollable &&
                this.sheet.contains(
                    closestScrollable
                )
            ) {
                return closestScrollable;
            }

            /*
             * En dialogs simples el propio sheet
             * puede ser el elemento con scroll.
             */
            return this.sheet;
        }

        onTouchStart(event) {
            if (!this.isMobile()) {
                return;
            }

            if (event.touches.length !== 1) {
                return;
            }
            const horizontalScroller =
                event.target.closest(
                    "[data-cart-nosto-viewport]"
                );

            if (horizontalScroller) {
                this.ignoreGesture = true;
                return;
            }

            this.ignoreGesture = false;

            this.activeScrollable =
                this.findScrollable(
                    event.target
                );

            this.startY =
                event.touches[0].clientY;

            this.startX =
                event.touches[0].clientX;

            this.currentY =
                this.startY;

            this.currentX =
                this.startX;

            this.startTime =
                performance.now();

            this.gestureDirection = null;
            this.isDragging = false;
            this.hasMoved = false;
        }

        onTouchMove(event) {
            if (this.ignoreGesture) {
                return;
            }
            if (!this.isMobile()) {
                return;
            }

            if (event.touches.length !== 1) {
                return;
            }

            const currentX =
                event.touches[0].clientX;

            const currentY =
                event.touches[0].clientY;

            const deltaX =
                currentX - this.startX;

            const deltaY =
                currentY - this.startY;

            const absX =
                Math.abs(deltaX);

            const absY =
                Math.abs(deltaY);

            this.currentX =
                currentX;

            this.currentY =
                currentY;

            if (!this.gestureDirection) {
                if (
                    absX < 6 &&
                    absY < 6
                ) {
                    return;
                }

                this.gestureDirection =
                    absX > absY
                        ? "horizontal"
                        : "vertical";
            }

            if (
                this.gestureDirection ===
                "horizontal"
            ) {
                return;
            }

            const distance =
                deltaY;

            if (distance <= 0) {
                return;
            }

            if (
                this.activeScrollable &&
                this.activeScrollable.scrollTop > 0
            ) {
                return;
            }

            if (
                !this.isDragging &&
                distance < 8
            ) {
                return;
            }

            if (!this.isDragging) {
                this.isDragging = true;

                this.sheet.classList.add(
                    "is-bottom-sheet-dragging"
                );

                this.sheet.classList.remove(
                    "is-bottom-sheet-returning"
                );
            }

            this.hasMoved = true;

            event.preventDefault();

            this.sheet.style.setProperty(
                "transform",
                `translate3d(0, ${distance}px, 0)`,
                "important"
            );
        }

        onTouchEnd() {
            if (this.ignoreGesture) {
                this.resetGestureState();
                return;
            }
            if (
                !this.isDragging ||
                !this.hasMoved
            ) {
                this.resetGestureState();
                return;
            }

            const distance =
                Math.max(
                    0,
                    this.currentY -
                    this.startY
                );

            const elapsed =
                Math.max(
                    1,
                    performance.now() -
                    this.startTime
                );

            const velocity =
                distance / elapsed;

            const threshold =
                Math.min(
                    150,
                    this.sheet.offsetHeight * 0.22
                );

            const shouldClose =
                distance >= threshold ||
                (
                    distance >= 50 &&
                    velocity >= 0.55
                );

            if (shouldClose) {
                this.animateSheetClose();
                return;
            }

            this.returnToPosition();
        }

        animateSheetClose() {
            const sheetHeight =
                this.sheet.getBoundingClientRect()
                    .height;

            const currentTransform =
                window.getComputedStyle(
                    this.sheet
                ).transform;

            const startTransform =
                currentTransform === "none"
                    ? `translate3d(0, ${Math.max(
                        0,
                        this.currentY -
                        this.startY
                    )
                    }px, 0)`
                    : currentTransform;

            const currentDistance =
                Math.max(
                    0,
                    this.currentY -
                    this.startY
                );

            const remainingDistance =
                Math.max(
                    0,
                    sheetHeight -
                    currentDistance
                );

            const prefersReducedMotion =
                window.matchMedia(
                    "(prefers-reduced-motion: reduce)"
                ).matches;

            const duration =
                prefersReducedMotion
                    ? 1
                    : Math.max(
                        120,
                        Math.min(
                            260,
                            remainingDistance * 0.38
                        )
                    );

            let hasFinished = false;

            this.sheet.classList.remove(
                "is-bottom-sheet-dragging"
            );

            this.sheet.classList.add(
                "is-bottom-sheet-closing"
            );

            const cartDrawer =
                this.sheet.closest(
                    "cart-drawer"
                );

            const dialog =
                this.sheet.closest(
                    "dialog"
                );

            const filterDrawer =
                this.sheet.matches(
                    "[data-collection-sidebar]"
                )
                    ? this.sheet
                    : this.sheet.closest(
                        "[data-collection-sidebar]"
                    );

            if (cartDrawer) {
                cartDrawer.classList.add(
                    "is-bottom-sheet-swipe-closing"
                );
            }

            if (dialog) {
                dialog.classList.add(
                    "is-bottom-sheet-swipe-closing"
                );
            }

            if (filterDrawer) {
                filterDrawer.classList.add(
                    "is-bottom-sheet-swipe-closing"
                );
            }

            this.sheet
                .getAnimations()
                .forEach((animation) => {
                    try {
                        animation.cancel();
                    } catch (error) {
                        // No hacemos nada.
                    }
                });

            this.sheet.style.setProperty(
                "transform",
                startTransform,
                "important"
            );

            this.sheet.getBoundingClientRect();

            const closeAnimation =
                this.sheet.animate(
                    [
                        {
                            transform:
                                startTransform,
                        },
                        {
                            transform:
                                `translate3d(0, ${sheetHeight + 4
                                }px, 0)`,
                        },
                    ],
                    {
                        duration,
                        easing:
                            "cubic-bezier(0.32, 0.72, 0, 1)",

                        fill:
                            "forwards",
                    }
                );

            const finishClose = () => {
                if (hasFinished) {
                    return;
                }

                hasFinished = true;
                this.closeSheet();

                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        try {
                            closeAnimation.cancel();
                        } catch (error) {
                            // No hacemos nada.
                        }

                        cartDrawer?.classList.remove(
                            "is-bottom-sheet-swipe-closing"
                        );

                        dialog?.classList.remove(
                            "is-bottom-sheet-swipe-closing"
                        );

                        if (
                            filterDrawer &&
                            filterDrawer !== this.sheet
                        ) {
                            filterDrawer.classList.remove(
                                "is-bottom-sheet-swipe-closing"
                            );
                        }

                        this.sheet.classList.remove(
                            "is-bottom-sheet-closing",
                            "is-bottom-sheet-swipe-closing"
                        );

                        this.sheet.style.removeProperty(
                            "transform"
                        );

                        this.sheet.style.removeProperty(
                            "transition"
                        );

                        this.resetGestureState();
                    });
                });
            };

            closeAnimation.finished
                .then(
                    finishClose
                )
                .catch(() => {
                    finishClose();
                });

            window.setTimeout(
                finishClose,
                duration + 150
            );
        }

        returnToPosition() {
            this.sheet.classList.remove(
                "is-bottom-sheet-dragging"
            );

            this.sheet.classList.add(
                "is-bottom-sheet-returning"
            );

            this.sheet.style.setProperty(
                "transform",
                "translate3d(0, 0, 0)",
                "important"
            );

            window.setTimeout(() => {
                this.sheet.classList.remove(
                    "is-bottom-sheet-returning"
                );

                this.sheet.style.removeProperty(
                    "transform"
                );
            }, 230);

            this.resetGestureState({
                preserveTransform: true,
            });
        }

        closeSheet() {

            /*
 * ---------------------------------------------------------
 * Nosto Quick Buy
 * ---------------------------------------------------------
 */
            const quickBuyModal =
                this.sheet.closest(
                    "[data-nosto-quick-buy-modal]"
                );

            if (quickBuyModal) {
                const controller =
                    window.NOSTO_QUICK_BUY_MODAL;

                if (
                    controller &&
                    typeof controller.close ===
                    "function"
                ) {
                    controller.close();
                    return;
                }

                /*
                 * Fallback por si el controller global
                 * todavía no está disponible.
                 */
                const closeButton =
                    quickBuyModal.querySelector(
                        "[data-nosto-quick-buy-close]"
                    );

                if (closeButton) {
                    closeButton.click();
                    return;
                }
            }
            /*
             * ---------------------------------------------------------
             * Complete Look
             * ---------------------------------------------------------
             */
            const completeLook =
                this.sheet.closest(
                    "[data-complete-look]"
                );

            if (completeLook) {
                const closeButton =
                    completeLook.querySelector(
                        "[data-complete-look-drawer-close]"
                    );

                if (closeButton) {
                    closeButton.click();
                    return;
                }
            }


            /*
             * ---------------------------------------------------------
             * Cart
             * ---------------------------------------------------------
             */
            const cartDrawer =
                this.sheet.closest(
                    "cart-drawer"
                );

            if (cartDrawer) {
                const closeButton =
                    cartDrawer.querySelector(
                        "[data-cart-drawer-close]"
                    );

                if (closeButton) {
                    closeButton.click();
                    return;
                }
            }


            /*
             * ---------------------------------------------------------
             * Filters
             * ---------------------------------------------------------
             */
            const filterDrawer =
                this.sheet.matches(
                    "[data-collection-sidebar]"
                )
                    ? this.sheet
                    : this.sheet.closest(
                        "[data-collection-sidebar]"
                    );

            if (filterDrawer) {
                const closeButton =
                    filterDrawer.querySelector(
                        "[data-collection-sidebar-close]"
                    );

                if (closeButton) {
                    closeButton.click();
                    return;
                }

                /*
                 * En algunos templates el underlay/close
                 * puede estar fuera del propio sidebar.
                 */
                const externalClose =
                    document.querySelector(
                        "[data-collection-sidebar-close]"
                    );

                externalClose?.click();

                return;
            }


            /*
             * ---------------------------------------------------------
             * PopupComponent / dialogs genéricos
             * ---------------------------------------------------------
             */
            const dialog =
                this.sheet.closest(
                    "dialog"
                );

            if (dialog) {
                const closeButton =
                    dialog.querySelector(
                        "[data-popup-close]"
                    );

                if (closeButton) {
                    closeButton.click();
                    return;
                }

                /*
                 * Fallback.
                 *
                 * Evitamos utilizar dialog.close() directamente cuando
                 * existe un sistema del theme, porque perderíamos su
                 * cleanup de scroll/focus.
                 */
                dialog.dispatchEvent(
                    new KeyboardEvent(
                        "keydown",
                        {
                            key: "Escape",
                            code: "Escape",
                            bubbles: true,
                            cancelable: true,
                        }
                    )
                );
            }
        }

        resetGestureState({
            preserveTransform = false,
        } = {}) {
            this.startX = 0;
            this.currentX = 0;

            this.startY = 0;
            this.currentY = 0;

            this.startTime = 0;

            this.isDragging = false;
            this.hasMoved = false;
            this.ignoreGesture = false;

            this.gestureDirection = null;

            this.activeScrollable = null;

            if (!preserveTransform) {
                this.sheet.classList.remove(
                    "is-bottom-sheet-dragging"
                );
            }
        }
    }


    /* ==========================================================================
       Initialization
       ========================================================================== */

    function initializeBottomSheets(
        root = document
    ) {
        const selector = SHEET_SELECTORS.join(",");
        const sheets = new Set(root.querySelectorAll(selector));

        if (root instanceof Element && root.matches(selector)) {
            sheets.add(root);
        }

        sheets.forEach((sheet) => {
            if (
                sheet.dataset
                    .mobileBottomSheetInitialized ===
                "true"
            ) {
                return;
            }

            sheet.dataset
                .mobileBottomSheetInitialized =
                "true";

            new MobileBottomSheet(
                sheet
            );
        });
    }


    /*
     * Primera carga.
     */
    if (
        document.readyState === "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            () => {
                initializeBottomSheets();
            }
        );
    } else {
        initializeBottomSheets();
    }


    /*
     * Shopify Theme Editor.
     */
    document.addEventListener(
        "shopify:section:load",
        (event) => {
            initializeBottomSheets(
                event.target
            );
        }
    );


    /*
     * Por si algún componente es inyectado/reemplazado
     * dinámicamente después del load.
     */
    const observer =
        new MutationObserver(
            (mutations) => {
                for (const mutation of mutations) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === 1) {
                            initializeBottomSheets(node);
                        }
                    }
                }
            }
        );

    observer.observe(
        document.documentElement,
        {
            childList: true,
            subtree: true,
        }
    );
})();